import { useRef } from "react";

import {
  classifyAccessFailure,
  useReplaceSubjectRolesMutation,
} from "@/api/access";
import {
  ROLE_LABEL,
  identityName,
  type SubjectAccessRow,
  type SubjectIdentity,
} from "@/lib/editorAccess";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { useToast } from "@/ui/Toast";

import { EFFECT_TIMING, SELF_ADMIN_LOCK_REASON } from "./GrantAccessDialog";

/** What removal does and does not do — stated on the confirmation, verbatim. */
export const REMOVAL_STATEMENTS = [
  "The Entra account is not deleted.",
  "The GitHub account is not changed.",
  "All active Clinical Editor role assignments for this person will be revoked.",
  "Historical grant and revocation records remain available for audit.",
] as const;

/**
 * "Remove Editor access": revoke every live role this person holds, in one
 * request, after an explicit confirmation that names them and their roles.
 *
 * The request is `PUT /v1/subject-roles` with an empty set and the live
 * assignment ids the table showed — the same atomic write the other dialogs
 * use — so a grant added by someone else meanwhile is a refusal, not a
 * silent survivor. An administrator's own row keeps the service's rule in
 * view: the confirm control is disabled with the reason, and the 409 the
 * service would answer stays in place behind it.
 */
export function RemoveAccessDialog({
  row,
  identity,
  self,
  onClose,
}: {
  row: SubjectAccessRow;
  identity: SubjectIdentity;
  self: { subject: string; displayName: string };
  onClose: () => void;
}) {
  const toast = useToast();
  const mutation = useReplaceSubjectRolesMutation({ selfSubject: self.subject });
  const busy = mutation.isPending;
  const inFlight = useRef(false);
  const name = identityName(identity);
  const selfAdmin = row.subject === self.subject && row.roles.includes("admin");

  const failure = mutation.isError ? classifyAccessFailure(mutation.error) : null;
  const mustReread = failure?.kind === "stale" || failure?.kind === "ambiguous";

  let alert: string | null = null;
  let mono: string | null = null;
  if (failure) {
    switch (failure.kind) {
      case "stale":
        alert =
          "This person's access was changed by someone else since it was read. The table has been refreshed — close this dialog and review their current access before deciding again.";
        break;
      case "self-admin":
        alert = failure.message;
        break;
      case "redundant":
      case "error":
        alert = `The Editor Service did not accept this change. Nothing was changed. ${failure.message}`;
        mono =
          failure.kind === "error"
            ? `${failure.code}${failure.requestId ? ` · ${failure.requestId}` : ""}`
            : null;
        break;
      case "denied":
        alert = "Your account is no longer authorized to manage Editor access.";
        break;
      case "unauthenticated":
        alert = "Your session has expired. Sign in again to continue.";
        break;
      case "directory-unavailable":
      case "unavailable":
        alert = "Access administration is not available in this environment.";
        break;
      case "ambiguous":
        alert =
          "The Editor Service's answer was interrupted, so the result is unknown. The table has been refreshed — close this dialog and check their access before deciding again.";
        mono = `${failure.code}${failure.requestId ? ` · ${failure.requestId}` : ""}`;
        break;
    }
  }

  async function confirm() {
    if (inFlight.current || busy || selfAdmin || mustReread) {
      return;
    }
    inFlight.current = true;
    try {
      await mutation.mutateAsync({
        subject: row.subject,
        roles: [],
        expectedAssignmentIds: row.assignments.map((a) => a.assignmentId),
      });
      toast(`Editor access removed for ${name}. ${EFFECT_TIMING}`);
      onClose();
    } catch {
      // Rendered from mutation.error above.
    } finally {
      inFlight.current = false;
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose();
        }
      }}
    >
      <DialogContent
        title={`Remove Editor access for ${name}?`}
        description="This revokes every Clinical Editor role this person holds. It does not delete or change any account."
        busy={busy}
        footer={
          <>
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void confirm()}
              disabled={busy || selfAdmin || mustReread}
              title={selfAdmin ? SELF_ADMIN_LOCK_REASON : undefined}
            >
              {busy ? "Removing…" : "Remove Editor access"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <section
            aria-label="Selected person"
            className="rounded-md border border-border bg-muted/40 p-3"
          >
            <p className="text-sm font-medium text-foreground">
              {name}
              {identity.self ? (
                <Badge tone="outline" className="ml-2">
                  You
                </Badge>
              ) : null}
            </p>
            {identity.email ? (
              <p className="text-xs text-muted-foreground">{identity.email}</p>
            ) : null}
            <p className="mt-1 text-xs text-muted-foreground">
              Entra Object ID:{" "}
              <span className="select-all font-mono text-foreground">
                {row.subject}
              </span>
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-muted-foreground">
                Roles being revoked:
              </span>
              {row.roles.map((role) => (
                <Badge key={role} tone="neutral">
                  {ROLE_LABEL[role]}
                </Badge>
              ))}
            </div>
          </section>

          <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
            {REMOVAL_STATEMENTS.map((statement) => (
              <li key={statement}>{statement}</li>
            ))}
          </ul>

          {selfAdmin ? (
            <p role="status" className="text-sm text-foreground">
              {SELF_ADMIN_LOCK_REASON} The Editor Service refuses this
              revocation as well, so it cannot happen by accident.
            </p>
          ) : null}

          {alert ? (
            <p role="alert" className="text-sm text-destructive">
              {alert}
            </p>
          ) : null}
          {mono ? (
            <p className="font-mono text-xs text-muted-foreground">{mono}</p>
          ) : null}

          <p className="text-xs text-muted-foreground">
            The change takes effect on the person's next authenticated request.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
